// "This army cannot win from here", said on the board, while there is still
// time to do something about it.
//
// Despair ran in complete silence: a side was diagnosed, given
// `RULES.despairTurns` to find a way out, and then swept into scrap. Nothing
// on screen changed until the army vanished — so when the side was the
// player's, the run ended with no warning and no stated reason, and when it
// was the opponent's, a fight you had already won looked like one that ended
// by magic.
//
// Three things it has to say, and the third is the one that makes it a warning
// rather than an obituary:
//
//   - **whose army**, since either side can be the one in the coma;
//   - **how long**, from the same subtraction the reducer's `surrender` gate
//     uses, so the number on screen is the number that will be acted on;
//   - **what would clear it**, as the two live counts from `destitute` itself.
//     A countdown with no way out is a punishment; these are the exact numbers
//     the diagnosis is a comparison between, and watching the material one
//     climb as you dig is the whole point.
//
// A banner rather than a modal, deliberately. The way out of this is to *play*
// — walk the king onto a heap, take a piece — and a dialog that has to be
// dismissed before you can do any of that would be in the way of its own
// advice. It sits over the board and takes no pointer events at all.

import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { RULES } from "src/engine/rules/rules";
import {
  type ChromeRect,
  keyframes,
  Panel,
  REDUCED_MOTION,
  Stack,
  STILL,
  styled,
  Text,
  useChromeRect,
  useChromeRects,
  useEdgeInsets,
  useFreeArea,
  useInsetStore,
  type Viewport
} from "./kit";

/**
 * Where a top-centre banner may stand: how far down, and how wide.
 *
 * **The slot lives here because despair is the banner in this band.** A banner at
 * `top: 12` painted over the turn row's status chip (designer, U9 review), and
 * the fix is not a number: the readouts and the turn row are *measured* into
 * the inset registry, so the free span between them is a fact the banner can
 * read.
 *
 * The rule is a fixed point rather than a rule of thumb. At each candidate
 * height, everything still reaching below that line is an obstacle; the widest
 * **viewport-centred** box that clears them all is the room. Take it if it is
 * wide enough, or drop past the shallowest obstacle and ask again. Centred is
 * the load-bearing word — the free span between a 160px readout and a 490px
 * turn row is not centred on the screen, and a banner that is cannot use all
 * of it.
 */
export interface BannerSlot {
  /** Distance from the top of the viewport, px. */
  top: number;
  /** Cap on the banner's width, px. */
  maxWidth: number;
}

/** How much air a banner keeps from whatever it is dodging. */
const GAP = 12;

/**
 * Narrower than this and a sentence becomes a column.
 *
 * So it is the threshold for *dropping* rather than squeezing: a banner that
 * fits between the readouts and the turn row stays up in that band, and one
 * that would have to be a slot of text goes underneath them instead.
 */
const MIN_WIDTH = 300;

/** The widest viewport-centred box that clears everything in `blocking`. */
function centredRoom(blocking: readonly ChromeRect[], centre: number): number {
  let half = centre - GAP;
  for (const one of blocking) {
    const { x, width } = one.rect;
    // How close this piece comes to the centre line, from whichever side of it
    // the piece sits on. A piece straddling the centre answers negative, which
    // is the honest reading: there is no centred box that clears it.
    const reach = x + width / 2 < centre ? centre - (x + width) : x - centre;
    half = Math.min(half, reach - GAP);
  }
  return Math.max(0, half * 2);
}

/**
 * `skip` is what this banner does not have to dodge — always its own entry.
 * **Two banners that each dodged the other would swap places forever**, so a
 * second banner in the band must skip the first, never both.
 */
export function bannerSlot(
  entries: readonly ChromeRect[],
  viewport: Viewport,
  want: number,
  skip: readonly string[]
): BannerSlot {
  // No registry yet — nothing has been measured, so nothing is in the way.
  if (viewport.width <= 0) return { top: GAP, maxWidth: want };

  const centre = viewport.width / 2;
  const others = entries.filter(
    (one) => !skip.includes(one.id) && one.rect.width > 0 && one.rect.height > 0
  );

  let top = GAP;
  // Bounded: every pass moves `top` past the shallowest obstacle's bottom, and
  // `top` only ever increases, so an obstacle cleared once can never block
  // again. One pass per obstacle, plus the pass that answers.
  for (let pass = 0; pass <= others.length; pass++) {
    const blocking = others.filter((one) => one.rect.y + one.rect.height > top);
    const room = centredRoom(blocking, centre);
    if (blocking.length === 0 || room >= Math.min(want, MIN_WIDTH)) {
      return { top, maxWidth: Math.min(want, room) };
    }
    top = Math.min(...blocking.map((one) => one.rect.y + one.rect.height)) + GAP;
  }
  return { top, maxWidth: want };
}

/**
 * The slot, live, and the ref that keeps this banner's own rect honest.
 *
 * The viewport comes back out of the free area rather than off `window`: the
 * registry already tracks the window size and re-derives on resize, and a
 * second listener here would be a second answer that can disagree with the
 * camera's.
 *
 * **A move is not a resize, and that cost eight pixels.** `useChromeRect`
 * re-measures on mount, on the element resizing and on the window resizing —
 * none of which fires when a banner slides down because the chrome under it
 * changed. The stale rect is then what any *other* banner stacks against. So
 * this re-measures
 * on every commit; `InsetStore.set` ignores anything under half a pixel, so a
 * settled banner writes nothing and nothing is notified.
 */
export function useBanner(
  id: string,
  want: number,
  skip: readonly string[]
): {
  slot: BannerSlot;
  ref: (element: HTMLElement | null) => void;
  onAnimationEnd: () => void;
} {
  const entries = useChromeRects();
  const insets = useEdgeInsets();
  const free = useFreeArea();
  const store = useInsetStore();
  const node = useRef<HTMLElement | null>(null);
  const register = useChromeRect(id, null);
  const ref = useCallback(
    (element: HTMLElement | null) => {
      node.current = element;
      register(element);
    },
    [register]
  );

  /**
   * **A banner arrives by sliding, and a measurement taken mid-slide is wrong
   * by however far it still has to go.** Eight pixels, here — the despair
   * banner's `arrive` keyframe — because a finished animation produces no
   * commit and so no re-measure. Bumping this on `animationend` forces one.
   */
  // The value is never read — bumping it is the whole point, because a commit
  // is what runs the layout effect below.
  const [, setLanded] = useState(0);
  const onAnimationEnd = useCallback(() => setLanded((was) => was + 1), []);

  // Every commit, the landing included. `InsetStore.set` ignores anything under
  // half a pixel, so a settled banner writes nothing and wakes nobody.
  useLayoutEffect(() => {
    const element = node.current;
    if (!store || !element) return;
    const box = element.getBoundingClientRect();
    store.set(id, null, { x: box.left, y: box.top, width: box.width, height: box.height });
  });

  const viewport: Viewport = {
    width: free.x + free.width + insets.right,
    height: free.y + free.height + insets.bottom
  };
  return { slot: bannerSlot(entries, viewport, want, skip), ref, onAnimationEnd };
}

/**
 * Scoped through the kit's `keyframes` rather than written as an `@keyframes`
 * key in `sx` — emotion scopes the name, and a bare block silently never
 * applies.
 *
 * **The centring lives in the keyframes too.** `animation … both` keeps the
 * last frame, and a keyframe that sets `transform` replaces the whole property
 * — so an `arrive` that animated `translateY` alone dropped the `translateX`
 * and left the banner sitting half its own width right of centre,
 * permanently. Anything animating `transform` here has to carry the offset in
 * every frame.
 */
const arrive = keyframes`
  0%   { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

/**
 * The glow pulses; the box does not move.
 *
 * The rule `celebration-claim` was fixed under: an element whose box never
 * settles cannot be clicked by Playwright and is a worse target for a person.
 * Nothing here is clickable, but a banner that breathes over a board somebody
 * is reading is its own problem.
 */
const urgent = keyframes`
  0%, 100% { box-shadow: 0 0 0 transparent; }
  50%      { box-shadow: 0 0 18px var(--urgent); }
`;

/**
 * The banner.
 *
 * `z.banner` is the moments' rung — over the board and the pill, under the
 * pause menu and the modals. `mine` is the only variant: your own army's
 * diagnosis wears the rust edge and breathes, theirs is a plain reading.
 */
const Bar = styled(Panel, {
  shouldForwardProp: (prop: string) => prop !== "mine"
})<{ mine?: boolean }>(({ theme, mine }) => ({
  // **Fixed, not absolute.** The registry measures in viewport coordinates, so
  // a banner placed against its parent box would be dodging chrome it is not
  // actually measured against.
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: theme.kit.z.banner,
  // The board underneath stays live. The way out of this is to play, so
  // nothing here may be in the way of a click.
  pointerEvents: "none",
  // **Border-box**, or `maxWidth` caps the text and the padding and border sit
  // outside the cap — forty pixels of overhang onto the objectives panel the
  // cap was computed to clear.
  boxSizing: "border-box",
  padding: `${theme.kit.space.sm}px ${theme.kit.space.lg}px`,
  textAlign: "center",
  ...(mine ? { borderColor: theme.kit.color.rust } : {}),
  "--urgent": `color-mix(in srgb, ${theme.kit.color.rust} 45%, transparent)`,
  animation: `${arrive} ${theme.kit.motion.duration.draw}ms ease-out both${
    mine ? `, ${urgent} 2.2s ease-in-out ${theme.kit.motion.duration.draw}ms infinite` : ""
  }`,
  // Off, the banner still has to arrive *centred* — see `arrive`. So the
  // transform is restated rather than left to a keyframe that no longer runs.
  [REDUCED_MOTION]: { ...STILL, transform: "translateX(-50%)" }
}));

/** The id in this band, and how wide despair would like to be. */
const DESPAIR_ID = "despair-banner";
const WANT = 560;

export function DespairBanner({
  despair,
  /** Live counts from `destitute`'s own two tests — see the header. */
  escape
}: {
  /** Whose army, and how long it has: `despairView`'s row, by shape. */
  despair: { mine: boolean; turnsLeft: number };
  escape?: { pieces: number; material: number };
}) {
  const { mine, turnsLeft } = despair;
  // **Its own entry is registered `edge: null`**: a moment must never move
  // the camera.
  const { slot, ref, onAnimationEnd } = useBanner(DESPAIR_ID, WANT, [DESPAIR_ID]);
  return (
    <Bar
      ref={ref}
      onAnimationEnd={onAnimationEnd}
      data-testid="despair-banner"
      mine={mine}
      // In `style`, never `sx`: both move whenever a piece of chrome does, and
      // emotion mints a permanent class per distinct value.
      style={{ top: slot.top, maxWidth: slot.maxWidth }}
    >
      <Text
        data-testid="despair-headline"
        role="heading"
        tone={mine ? "rust" : "cream"}
        size={14}
        block
      >
        {mine ? "Your army has no way to win" : "Their army has no way to win"}
        {" · "}
        {turnsLeft === 0
          ? "laying down its arms"
          : `${turnsLeft} ${turnsLeft === 1 ? "turn" : "turns"} left`}
      </Text>

      {/* Only for your own. Being told how to rescue the opponent is advice
          for the wrong player, and the enemy's banner is information rather
          than a call to act. */}
      {mine && escape && (
        <>
          <Text size={12.5} block sx={{ mt: 0.5, lineHeight: 1.45 }}>
            It cannot reach a mating piece from here. Take a second real piece,
            or get metal onto ground you hold, and the diagnosis lifts.
          </Text>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 0.75 }}>
            <Need
              id="pieces"
              label="Pieces"
              have={escape.pieces}
              need={RULES.despairPieces}
            />
            <Need
              id="material"
              label="Metal in reach"
              have={escape.material}
              need={RULES.despairMaterial}
            />
          </Stack>
        </>
      )}
    </Bar>
  );
}

/**
 * One of the two ways out, as have-of-needed.
 *
 * Either one alone lifts it — `destitute` is an `&&`, so failing either test
 * is enough — and the wording has to carry that or a player reads two bars as
 * two bills to pay.
 */
function Need({
  id,
  label,
  have,
  need
}: {
  id: string;
  label: string;
  have: number;
  need: number;
}) {
  const done = have >= need;
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" data-testid={`despair-${id}`}>
      {/* The tone is the reading — met or still short — so it is a prop rather
          than a colour, and there are only two of them. */}
      <Text role="display" tone={done ? "cream" : "brand"} size={15} weight={700}>
        {have}
        <Text role="display" tone="dim" size={15} weight={500}>
          {` / ${need}`}
        </Text>
      </Text>
      <Text role="display" tone="dim" size={10} weight={700} sx={{ letterSpacing: "0.08em" }}>
        {label}
      </Text>
    </Stack>
  );
}
